import type { PanelConversationReason } from '@vaptmenu/analytics-react';
import { CONVERSATION_REASONS, type ConversationReason } from '../types/conversations.types';

/* ==========================================================================
   Motivos da conversa em pt-BR (design D18)

   Um mapa único para a lista, o resumo do transbordo, a faixa e a
   notificação. O painel é leitor tolerante: motivo que ele não conhece (a
   API saiu antes) aparece como "Outro motivo", sem quebrar a lista.
   ========================================================================== */

export const UNKNOWN_REASON_LABEL = 'Outro motivo';

export const REASON_LABELS: Readonly<Record<ConversationReason, string>> = {
  customer_requested: 'Cliente pediu atendente',
  complaint: 'Reclamação',
  delivered_order_issue: 'Problema com pedido entregue',
  refund_request: 'Pedido de reembolso',
  repeated_failure: 'O Atendente IA não conseguiu resolver',
  severe_allergy: 'Alergia grave',
  privacy_request: 'Pedido sobre dados pessoais',
  provider_unavailable: 'Atendente IA fora do ar',
  quota_exhausted: 'Limite de atendimentos atingido',
  budget_exceeded: 'Limite de uso do mês atingido',
  contact_limit_reached: 'Limite de mensagens do contato',
  pending_order_not_accepted: 'Pedido ainda não aceito',
  cancellation_request_escalated: 'Pedido de cancelamento',
  amendment_request_escalated: 'Pedido de alteração',
  late_order: 'Pedido atrasado',
  out_for_delivery_change: 'Mudança em pedido a caminho',
  unlinked_order_change: 'Mudança em pedido não identificado',
  image_received: 'Cliente mandou imagem',
  payment_refund_needed: 'Estorno de pagamento',
  message_not_delivered: 'Mensagem não entregue ao cliente',
  stale_message: 'Mensagem antiga sem resposta',
  out_of_rules: 'Fora das regras da loja',
  human_echo: 'Equipe respondeu pelo celular',
  panel_reply: 'Equipe respondeu pelo painel',
  assumed: 'Assumida pela equipe',
  recent_human_history: 'Equipe atendeu há pouco',
  customer_opt_out: 'Cliente pediu para não falar com o assistente',
  team_marked: 'Marcada "Só a equipe"',
  do_not_serve: 'Contato marcado "Não atender"',
  default: 'Atendimento pelo Atendente IA',
  ai_resumed_timeout: 'O Atendente IA voltou a atender',
  returned_by_team: 'Devolvida para o Atendente IA',
  customer_opted_in: 'Cliente voltou a falar com o assistente',
};

function isKnownReason(reason: string | null | undefined): reason is ConversationReason {
  return typeof reason === 'string' && Object.hasOwn(REASON_LABELS, reason);
}

/** O rótulo em pt-BR do motivo; "Outro motivo" para valor desconhecido ou ausente. */
export function reasonLabel(reason: string | null | undefined): string {
  return isKnownReason(reason) ? REASON_LABELS[reason] : UNKNOWN_REASON_LABEL;
}

/** O motivo para o analytics: o membro do enum, ou `other` quando o painel não o conhece. */
export function analyticsReason(reason: string | null | undefined): PanelConversationReason {
  return isKnownReason(reason) ? reason : 'other';
}

/** Todo motivo da API tem rótulo (o teste confere contra a lista). */
export const LABELLED_REASONS: readonly ConversationReason[] = CONVERSATION_REASONS;
